import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { routingConstants } from "../Constants/routingConstants";
import { _localGet, _localRemove, _localSet } from "../localStorage";
import { updateLoginAccessData } from "../reducers/auth";
import { setChatOpen } from "../reducers/chat";
import {
  setShowProfile,
  updateActiveDetailListing,
} from "../reducers/clientcandidatedetail";
import { handleSubBranchLogin } from "../reducers/session";
import { apiDecrypt, apiEncrypt } from "../utilities/keyEncryption";
import {
  showCommonToast,
  showErrorToast,
  showRoleAccessToast,
} from "../utilities/utils";

const EmailRedirectRouting = () => {
  const { user } = useSelector((state) => state.clientProfile);
  const { session } = useSelector((state) => state.session);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const tmsauth = _localGet("tmsauth");
    const vcomp = _localGet("vcomp");
    // vcont/clientId_contractId
    const vcont = _localGet("vcont");
    const vapplied = _localGet("vapplied");
    const newUserEmail = _localGet("userauth");
    // /appchat/client_name
    const appChat = _localGet("appchat");

    const decryptLocalStorageValue = (key, prefix) => {
      const extracted = _localGet(key)?.split(`${prefix}/`)[1] || "";
      return apiDecrypt(extracted) || "";
    };

    const getDecryptedBranches = () =>
      session?.client_sub_branches?.map((branch) => apiDecrypt(branch)) || [];

    const branchAccessDenied = (message, keyToRemove) => {
      showErrorToast(message, { cls: "!max-w-[400px]" });
      _localRemove(keyToRemove);
    };

    const loginToSubBranch = (clientIdFromUrl, isRedirect = 0) => {
      return dispatch(
        handleSubBranchLogin({
          branches: { client_branch_id: clientIdFromUrl },
          navigate,
          isRedirect: isRedirect,
        }),
      );
    };

    if (tmsauth) {
      const extractedPart = _localGet("tmsauth")?.split("tmsauth/")[1] || "";
      const decryptedValue = apiDecrypt(extractedPart) || "";

      if (decryptedValue === user?.client_user_email) {
        navigate(routingConstants?.submittedTimesheet);
        const timeout = setTimeout(() => _localRemove("tmsauth"), 2000);
        return () => clearTimeout(timeout); // Cleanup timeout
      }
    } else if (vcomp) {
      const decryptedData = decryptLocalStorageValue("vcomp", "vcomp");
      const [clientIdFromUrl, candidateNameFromUrl] =
        decryptedData.split("_") || [];
      const encryptedCandidateName = apiEncrypt(candidateNameFromUrl) || "";

      const setupComplianceView = () => {
        _localSet("activeDetailListing", "compliance");
        dispatch(setShowProfile(true));
        dispatch(updateActiveDetailListing("compliance"));
      };

      const navigateToCandidateDetail = () => {
        navigate(routingConstants?.candidateDetail(encryptedCandidateName));
      };

      const clearVcomp = () => setTimeout(() => _localRemove("vcomp"), 2000);

      if (clientIdFromUrl == user?.client_id) {
        if (session?.allPermissions?.is_allow_to_view_candidate_profile) {
          setupComplianceView();
          navigateToCandidateDetail();
        } else {
          showRoleAccessToast();
        }
        clearVcomp();
        return;
      } else {
        if (user?.is_headoffice) {
          const matchedBranch = getDecryptedBranches()?.find(
            (branch) => branch === clientIdFromUrl,
          );

          if (!matchedBranch) {
            branchAccessDenied(
              "Access restricted: Please switch to the correct branch to view this candidate compliance.",
              "vcomp",
            );
            return;
          }

          loginToSubBranch(clientIdFromUrl);

          if (session?.allPermissions?.is_allow_to_view_candidate_profile) {
            navigateToCandidateDetail();
            setupComplianceView();
            setTimeout(() => {
              _localSet("vcomp_differnt_branch", "true");
            }, 600);
          } else {
            showRoleAccessToast();
          }

          _localRemove("vcomp");
        }
        // else {
        //     showErrorToast("Access restricted: Please switch to the correct branch to view this candidate compliance.", {
        //         id: "vcomp"
        //     });
        //     _localRemove("vcomp");
        // }
      }
    } else if (vcont) {
      const decryptedData = decryptLocalStorageValue("vcont", "vcont");
      const [clientIdFromUrl, contractIdFromUrl] =
        decryptedData.split("_") || [];
      const encryptedContractId = apiEncrypt(contractIdFromUrl) || "";

      const navigateToContractDetail = () => {
        navigate(routingConstants?.contractDetail(encryptedContractId));
      };

      const clearVcont = () => setTimeout(() => _localRemove("vcont"), 2000);

      const handleContractNavigation = () => {
        if (session?.allPermissions?.is_allow_to_view_contract_detail) {
          navigateToContractDetail();
        } else {
          showRoleAccessToast();
        }
      };

      if (clientIdFromUrl == user?.client_id) {
        handleContractNavigation();
        clearVcont();
        return;
      } else {
        if (user?.is_headoffice) {
          const matchedBranch = getDecryptedBranches()?.find(
            (branch) => branch === clientIdFromUrl,
          );

          if (!matchedBranch) {
            branchAccessDenied(
              "Access restricted: Please switch to the correct branch to view this contract.",
              "vcont",
            );
            return;
          }

          loginToSubBranch(clientIdFromUrl);
          handleContractNavigation();
          _localSet("vcont_differnt_branch", "true");
          _localRemove("vcont");
        }
        // else {
        //     showErrorToast("Access restricted: Please switch to the correct branch to view this contract.", {
        //         id: "vcont"
        //     });
        //     _localRemove("vcont");

        // }
      }
    } else if (newUserEmail) {
      const extractedPart = newUserEmail?.split("userauth/")[1] || "";
      const decryptClientName = apiDecrypt(extractedPart) || "";
      dispatch(
        updateLoginAccessData({
          email: decryptClientName,
        }),
      );
      // delete after 5 min
      const timeout = setTimeout(() => {
        _localRemove("userauth");
      }, 300000);
      return () => clearTimeout(timeout);
    }

    if (appChat) {
      const extractedPart = appChat.split("appchat/")[1] || "";
      const decryptClientName = apiDecrypt(extractedPart) || "";
      const loginClientId = user?.client_id || "";

      if (loginClientId == decryptClientName) {
        const handleChatActions = () => {
          if (session?.allPermissions?.is_allow_to_chat) {
            dispatch(setChatOpen(true));
            showCommonToast(
              "A candidate's unread message is waiting for you.",
              {
                id: "appchat",
              },
            );
          }
          _localRemove("appchat");
        };

        const timers = [setTimeout(handleChatActions, 2000)];

        return () => timers.forEach(clearTimeout);
      }
    }

    const checkAppliedCandidate = async () => {
      if (vapplied) {
        const decryptedData = decryptLocalStorageValue("vapplied", "vapplied");
        const [clientIdFromUrl] = decryptedData.split("_") || [];

        const navigateToapppliedPage = () => {
          navigate(routingConstants?.appliedCandidate);
        };

        const clearVapplied = () =>
          setTimeout(() => _localRemove("vapplied"), 2000);

        const handleAppliedNavigation = () => {
          if (session?.allPermissions?.is_allow_to_applied_list == 1) {
            navigateToapppliedPage();
          } else {
            showRoleAccessToast();
          }
        };

        if (clientIdFromUrl == user?.client_id) {
          handleAppliedNavigation();
          clearVapplied();
          return;
        } else {
          if (user?.is_headoffice) {
            const matchedBranch = getDecryptedBranches()?.find(
              (branch) => branch == clientIdFromUrl,
            );

            if (!matchedBranch) {
              branchAccessDenied(
                "Access restricted: Please switch to the correct branch to view this applied candidate.",
                "vapplied",
              );
              return;
            }

            try {
              await loginToSubBranch(clientIdFromUrl, 1);
              setTimeout(() => {
                handleAppliedNavigation();
                _localSet("vapplied_differnt_branch", "true");
                _localRemove("vapplied");
              }, 3500);
            } catch (error) {
              console.error("Error during branch login:", error);
              showErrorToast("Branch login failed. Please try again.");
            }
          }
          // else {
          //     showErrorToast("Access restricted: Please switch to the correct branch to view this applied candidate.", {
          //         id: "applied"
          //     });
          //     setTimeout(() => {
          //         _localRemove("vapplied");
          //     }, 2000)
          // }
        }
      }
    };

    checkAppliedCandidate();
  }, [user?.client_id, user?.is_headoffice]);
  return null;
};

export default EmailRedirectRouting;
